import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  Calendar, 
  Search, 
  CheckSquare,
  Square,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Filter,
  ChevronDown,
  User,
  Phone,
  Car,
  UserCog,
  Wrench
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Venter', color: 'bg-yellow-600', icon: Clock },
  { value: 'confirmed', label: 'Bekreftet', color: 'bg-green-600', icon: CheckCircle },
  { value: 'completed', label: 'Fullført', color: 'bg-blue-600', icon: CheckCircle },
  { value: 'cancelled', label: 'Kansellert', color: 'bg-red-600', icon: XCircle },
  { value: 'no_show', label: 'Ikke møtt', color: 'bg-gray-600', icon: AlertTriangle }
];

const AdminBookingsBulk = () => {
  const { token, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processing, setProcessing] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showMechanicMenu, setShowMechanicMenu] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/bookings`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Feil ved henting av bookinger:', error);
      setMessage({ type: 'error', text: 'Kunne ikke hente bookinger' });
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const fetchMechanics = useCallback(async () => {
    try {
      // Use the mechanics endpoint that's available to all users
      const response = await axios.get(`${API}/mechanics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMechanics(response.data);
    } catch (error) {
      console.error('Could not fetch mechanics:', error);
      setMechanics([]);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
    fetchMechanics();
  }, [fetchBookings, fetchMechanics]);

  // Filter bookings by search
  const filteredBookings = bookings.filter(booking => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      booking.customer_name?.toLowerCase().includes(query) ||
      booking.customer_email?.toLowerCase().includes(query) ||
      booking.customer_phone?.includes(query) ||
      booking.booking_number?.toLowerCase().includes(query) ||
      booking.reg_number?.toLowerCase().includes(query)
    );
  });

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredBookings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBookings.map(b => b.id)));
    }
  };

  // Toggle single selection
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Bulk update status
  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'warning', text: 'Velg minst én booking' });
      return;
    }

    setProcessing(true);
    setShowStatusMenu(false);
    
    try {
      const response = await axios.post(`${API}/bookings/bulk/status`, {
        booking_ids: Array.from(selectedIds),
        new_status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      setMessage({
        type: result.failed > 0 ? 'warning' : 'success',
        text: `Oppdatert ${result.updated} av ${result.total} bookinger${result.failed > 0 ? `. ${result.failed} feilet.` : ''}`
      });

      setSelectedIds(new Set());
      fetchBookings();
    } catch (error) {
      console.error('Feil ved bulk-oppdatering:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Kunne ikke oppdatere bookinger' });
    } finally {
      setProcessing(false);
    }
  };

  // Bulk assign mechanic
  const handleBulkAssignMechanic = async (mechanicEmail) => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'warning', text: 'Velg minst én booking' });
      return;
    }

    setProcessing(true);
    setShowMechanicMenu(false);
    
    try {
      const response = await axios.post(`${API}/bookings/bulk/field`, {
        booking_ids: Array.from(selectedIds),
        field: 'assigned_mechanic',
        value: mechanicEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      setMessage({
        type: result.failed > 0 ? 'warning' : 'success',
        text: `Tildelt mekaniker til ${result.updated} av ${result.total} bookinger${result.failed > 0 ? `. ${result.failed} feilet.` : ''}`
      });

      setSelectedIds(new Set());
      fetchBookings();
    } catch (error) {
      console.error('Feil ved tildeling av mekaniker:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Kunne ikke tildele mekaniker' });
    } finally {
      setProcessing(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'warning', text: 'Velg minst én booking' });
      return;
    }

    if (!window.confirm(`Er du sikker på at du vil slette ${selectedIds.size} booking(er)? Dette kan ikke angres.`)) {
      return;
    }

    setProcessing(true);
    
    try {
      const response = await axios.post(`${API}/bookings/bulk/delete`, {
        booking_ids: Array.from(selectedIds)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      setMessage({
        type: result.failed > 0 ? 'warning' : 'success',
        text: `Slettet ${result.deleted} av ${result.total} bookinger${result.failed > 0 ? `. ${result.failed} feilet.` : ''}`
      });

      setSelectedIds(new Set());
      fetchBookings();
    } catch (error) {
      console.error('Feil ved sletting:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Kunne ikke slette bookinger' });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    if (!option) return <Badge className="bg-gray-600">{status}</Badge>;
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Masseredigering av Bookinger</h1>
          <p className="text-gray-400 mt-1">Velg flere bookinger og utfør handlinger på alle samtidig</p>
        </div>
        <Button 
          onClick={fetchBookings}
          variant="outline" 
          className="border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Oppdater
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-900/50 border border-green-600 text-green-300' :
          message.type === 'warning' ? 'bg-yellow-900/50 border border-yellow-600 text-yellow-300' :
          'bg-red-900/50 border border-red-600 text-red-300'
        }`}>
          {message.text}
          <button 
            onClick={() => setMessage(null)} 
            className="float-right hover:opacity-70"
          >
            ×
          </button>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søk på navn, e-post, telefon, booking-nummer..."
                className="pl-10 bg-gray-900 border-gray-600 text-white placeholder:text-gray-500"
                data-testid="bulk-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
            >
              <option value="all">Alle statuser</option>
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      <Card className="bg-gray-800/50 border-gray-700 sticky top-0 z-10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-gray-300 hover:text-white"
                data-testid="select-all-btn"
              >
                {selectedIds.size === filteredBookings.length && filteredBookings.length > 0 ? (
                  <CheckSquare className="w-5 h-5 text-red-500" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
                <span>
                  {selectedIds.size > 0 
                    ? `${selectedIds.size} valgt` 
                    : 'Velg alle'
                  }
                </span>
              </button>
            </div>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                {/* Status dropdown */}
                <div className="relative">
                  <Button
                    onClick={() => { setShowStatusMenu(!showStatusMenu); setShowMechanicMenu(false); }}
                    disabled={processing}
                    variant="outline"
                    className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                    data-testid="change-status-btn"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Endre status
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {showStatusMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-20">
                      {STATUS_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => handleBulkStatusUpdate(opt.value)}
                          className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-800 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                        >
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mechanic assignment dropdown */}
                <div className="relative">
                  <Button
                    onClick={() => { setShowMechanicMenu(!showMechanicMenu); setShowStatusMenu(false); }}
                    disabled={processing}
                    variant="outline"
                    className="border-purple-600 text-purple-400 hover:bg-purple-900/30"
                    data-testid="assign-mechanic-btn"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Tildel mekaniker
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {showMechanicMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                      {/* Clear assignment option */}
                      <button
                        onClick={() => handleBulkAssignMechanic('')}
                        className="w-full px-4 py-2 text-left text-gray-400 hover:bg-gray-800 flex items-center gap-2 border-b border-gray-700"
                      >
                        <XCircle className="w-4 h-4" />
                        Fjern tildeling
                      </button>
                      
                      {/* List of mechanics */}
                      {mechanics.length > 0 ? (
                        mechanics.map(mech => (
                          <button
                            key={mech.id}
                            onClick={() => handleBulkAssignMechanic(mech.email)}
                            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                          >
                            <UserCog className="w-4 h-4" />
                            <div>
                              <div>{mech.full_name}</div>
                              <div className="text-xs text-gray-500">{mech.email}</div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          Ingen mekanikere funnet. Kontakt admin.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Delete button (only for admin) */}
                {(user?.role === 'master_admin' || user?.role === 'company_owner') && (
                  <Button
                    onClick={handleBulkDelete}
                    disabled={processing}
                    variant="outline"
                    className="border-red-600 text-red-400 hover:bg-red-900/30"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Slett ({selectedIds.size})
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Booking List */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Bookinger ({filteredBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg">Ingen bookinger funnet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredBookings.map((booking) => (
                <div
                  key={booking.id}
                  className={`flex items-center gap-4 p-4 rounded-lg transition-all cursor-pointer ${
                    selectedIds.has(booking.id) 
                      ? 'bg-red-900/30 border border-red-600' 
                      : 'bg-gray-900/50 hover:bg-gray-900 border border-transparent'
                  }`}
                  onClick={() => toggleSelect(booking.id)}
                  data-testid={`booking-row-${booking.id}`}
                >
                  {/* Checkbox */}
                  <div className="flex-shrink-0">
                    {selectedIds.has(booking.id) ? (
                      <CheckSquare className="w-5 h-5 text-red-500" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-500" />
                    )}
                  </div>

                  {/* Booking info */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Date & Time */}
                    <div>
                      <p className="text-white font-semibold">{formatDate(booking.date)}</p>
                      <p className="text-gray-400 text-sm">{booking.time_slot} • Plass {booking.slot_number}</p>
                    </div>

                    {/* Customer */}
                    <div>
                      <div className="flex items-center gap-2 text-white">
                        <User className="w-4 h-4 text-gray-500" />
                        {booking.customer_name}
                      </div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Phone className="w-3 h-3" />
                        {booking.customer_phone}
                      </div>
                    </div>

                    {/* Vehicle */}
                    <div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Car className="w-4 h-4 text-gray-500" />
                        {booking.vehicle_type || 'Ukjent'}
                      </div>
                      {booking.reg_number && (
                        <p className="text-gray-500 text-sm">{booking.reg_number}</p>
                      )}
                    </div>

                    {/* Status & Booking number */}
                    <div className="flex items-center justify-between md:justify-end gap-3">
                      {getStatusBadge(booking.status)}
                      <span className="text-gray-500 text-sm">{booking.booking_number}</span>
                    </div>
                  </div>
                  {/* Show assigned mechanic if present */}
                  {booking.assigned_mechanic && (
                    <div className="mt-1 flex items-center gap-1 text-purple-400 text-xs">
                      <Wrench className="w-3 h-3" />
                      {booking.assigned_mechanic}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Click outside to close menus */}
      {(showStatusMenu || showMechanicMenu) && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => { setShowStatusMenu(false); setShowMechanicMenu(false); }}
        />
      )}
    </div>
  );
};

export default AdminBookingsBulk;
